import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { localizeSystemText } from '../../i18n/messages';
import { reminderReason } from "./PaymentReminderPreviewModal";
import LocalizedMessage from '../common/LocalizedMessage';
import { displayDate, displayDateTime } from "../common/datePresentation";
import { history } from "../../redux/store/configureStore";
import * as clientActions from "../../redux/actions/clientActions";
import NotificationService from "../../services/NotificationService";
import { getById } from "../common/Helpers";
import { formatLedgerAmount } from "./clientLedgerTypes";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";

const NOTIFICATION_TYPE_LABELS = {
  lesson_first_reminder: "Przypomnienie o pierwszych zajeciach",
  lesson_reminder: "Przypomnienie o zajeciach",
  payment_request: "Prosba o platnosc",
  payment_overdue: "Platnosc po terminie",
};

const formatValue = value => value || "-";

const getNotificationTypeLabel = type => NOTIFICATION_TYPE_LABELS[type] || type || "-";

const paymentDetails = notification => (
  notification.amountDue && notification.dueDate
    ? `${formatLedgerAmount(notification.amountDue)} / termin ${displayDate(notification.dueDate)}`
    : "-"
);

export function ClientNotificationHistoryPage({
  clients,
  client,
  loadClients,
  notificationService,
  schoolId,
}) {
  const { t: tr, i18n } = useTranslation();
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (clients.length === 0) {
      loadClients().catch(loadError => setError(loadError.message));
    }
  }, [clients.length, loadClients]);

  useEffect(() => {
    if (!client.id) return undefined;

    let cancelled = false;
    setNotifications([]); setError("");
    notificationService.getClientNotificationHistory(client.id)
      .then(value => { if (!cancelled) setNotifications(value); })
      .catch(loadError => { if (!cancelled) setError(loadError.message); });
    return () => { cancelled = true; };
  }, [client.id, schoolId, notificationService]);

  if (!client.id) {
    return <div className="manage-text">{tr("manage:loadingClient_127")}</div>;
  }

  return (
    <div className="mb-5">
      <h2 className="manage-title">{tr('common:notificationHistory', { name: `${client.firstName || ''} ${client.lastName || ''}`.trim() })}</h2>
      {error && <div role="alert" className="alert alert-danger"><LocalizedMessage isError value={error} /></div>}
      {notifications.length === 0 ? (
        <div className="manage-text">{tr("manage:noNotificationHistoryForThisClient_144")}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm manage-text">
            <thead>
              <tr>
                <th scope="col">{tr("manage:type_129")}</th>
                <th scope="col">{tr("manage:channel_145")}</th>
                <th scope="col">{tr("manage:paymentReminderV1.origin")}</th>
                <th scope="col">{tr("manage:paymentReminderV1.reason")}</th>
                <th scope="col">{tr("manage:status_146")}</th>
                <th scope="col">{tr("manage:scheduled_147")}</th>
                <th scope="col">{tr("manage:sent_148")}</th>
                <th scope="col">{tr("manage:details_149")}</th>
                <th scope="col">{tr("manage:error_150")}</th>
                <th scope="col">{tr("manage:providerID_151")}</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map(notification => (
                <tr key={notification.id}>
                  <td>{localizeSystemText(getNotificationTypeLabel(notification.notificationType), tr)}</td>
                  <td>{formatValue(notification.channel)}</td>
                  <td>{tr(`manage:paymentReminderV1.${notification.origin || "automatic"}`, { defaultValue: notification.origin || "automatic" })}<br />{notification.actorId || ""}</td>
                  <td>{notification.reason || "�"}{(notification.reasonCodes || []).map(code => <div key={code}>{reminderReason(tr, code)}</div>)}</td>
                  <td>{notification.status}{notification.notificationType && notification.notificationType.startsWith("payment_") && <div>{tr(`manage:paymentReminderV1.states.${notification.status}`, { defaultValue: notification.status })}</div>}</td>
                  <td>{formatValue(displayDateTime(notification.scheduledFor, i18n.language))}</td>
                  <td>{formatValue(displayDateTime(notification.sentAt, i18n.language))}</td>
                  <td>{paymentDetails(notification)}</td>
                  <td>{formatValue(notification.error)}</td>
                  <td>{formatValue(notification.providerMessageId)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button
        type="button"
        className="btn manage-button manage-teal-button mt-3"
        onClick={() => history.push(buildManageRouteWithSchoolYear(`/manage/client/${client.id}`, location.search, activeSchoolYearStartYear))}
      >{tr("manage:back_128")}
      </button>
    </div>
  );
}

ClientNotificationHistoryPage.propTypes = {
  schoolId: PropTypes.string,
  clients: PropTypes.array.isRequired,
  client: PropTypes.object.isRequired,
  loadClients: PropTypes.func.isRequired,
  notificationService: PropTypes.shape({
    getClientNotificationHistory: PropTypes.func.isRequired,
  }),
};

ClientNotificationHistoryPage.defaultProps = {
  notificationService: NotificationService,
  schoolId: null,
};

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  return {
    clients: state.clients,
    schoolId: state.user && state.user.schoolId,
    client: getById(state.clients, id) || {},
  };
};

export default connect(mapStateToProps, {
  loadClients: clientActions.loadClients,
})(ClientNotificationHistoryPage);
